import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';

import PackageJson from '../../../package.json' with { type: 'json' };
import { DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN, IS_PRODUCTION } from '../../config/Environment';

interface IDatadogUserTraits {
	readonly [propName: string]: string | number | boolean;
}

export interface IDatadogLogsModel {
	readonly initialize: VoidFunction;
	readonly identify: (navinaUserId: string, traits: IDatadogUserTraits) => void;
	readonly captureException: (error: Error, options?: Record<string, unknown>) => void;
}

export class DatadogLogsModel implements IDatadogLogsModel {
	private readonly service: string;

	// Modify constructor to accept the service name
	constructor(service: string) {
		this.service = service;
	}

	static readonly create = (service: string): DatadogLogsModel => new DatadogLogsModel(service);

	readonly initialize = (): void => {
		if (!DATADOG_CLIENT_TOKEN) {
			throw new Error('DATADOG_CLIENT_TOKEN is not set. Cannot initialize Datadog.');
		}

		const { version: packageJsonVersion } = PackageJson;

		const datadogOptions: LogsInitConfiguration = {
			clientToken: DATADOG_CLIENT_TOKEN,
			site: 'datadoghq.com',
			forwardErrorsToLogs: false,
			forwardConsoleLogs: ['error'],
			sessionSampleRate: 100,
			service: this.service,
			env: IS_PRODUCTION ? 'production' : 'development',
			version: packageJsonVersion,
		};

		datadogLogs.init(datadogOptions);
	};

	readonly identify = (navinaUserId: string, traits: IDatadogUserTraits): void => {
		datadogLogs.setUser({
			id: navinaUserId,
			...traits,
		});
	};

	readonly captureException = (error: Error, options?: Record<string, unknown>): void => {
		datadogLogs.logger.error(error.message, { ...options, error });
	};
}

let maybeDatadogLogsModel: IDatadogLogsModel | null = null;

export function getOrCreateDatadogLogsModel(service: string = 'navina-frontend-app'): IDatadogLogsModel {
	if (!maybeDatadogLogsModel) {
		maybeDatadogLogsModel = DatadogLogsModel.create(service);

		if (DATADOG_CLIENT_TOKEN && DATADOG_APPLICATION_ID) {
			maybeDatadogLogsModel.initialize();
		}
	}

	return maybeDatadogLogsModel;
}
